/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";
import { makeClientTool } from "@zapier/ai-toolkit";

interface SearchResult {
  chunk: string;
  similarity: number;
}

export const SearchToolParams = z.object({
  query: z
    .string()
    .describe("The search query to find similar API specs chunks"),
  source: z
    .string()
    .describe(
      "source filter to limit search to specific api spec sources: chatbots, tables, interfaces, zapguesser, gulliver (for zaps)",
    ),
});

export type SearchToolParamsType = z.infer<typeof SearchToolParams>;

async function searchSimilarChunks(
  query: string,
  source?: string,
): Promise<SearchResult[]> {
  try {
    const response = await fetch("/api/canvasui/search", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        source: source,
      }),
    });

    if (!response.ok) {
      throw new Error(`Search request failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search request failed");
    }

    return data.results;
  } catch (error) {
    console.error("Error searching similar chunks:", error);
    throw error;
  }
}

export const searchToolExecute = async (rawArgs: any) => {
  let params: SearchToolParamsType;
  try {
    params = SearchToolParams.parse(rawArgs || {});
  } catch (error) {
    console.error(
      "[SEARCH_TOOL] Error parsing arguments for searchTool:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for search_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const { query, source } = params;

  try {
    const results = await searchSimilarChunks(query, source);
    return {
      content: [
        {
          type: "text",
          // Return only the top 3 results, formatted as pretty JSON
          text: JSON.stringify(results.slice(0, 9), null, 2),
        },
      ],
    };
  } catch (error) {
    console.error("[SEARCH_TOOL] Error in searchToolExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error searching similar chunks: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const searchToolDescription = `Searches for the most similar API specs and interface documentation that can help achieve the user's goal.
- query: The search query to find similar API specs and interface chunks - use before making a network request.
- source: Optional filter to search within specific embedding sources (chatbots, tables, interfaces, zapguesser, gulliver (for zaps), etc.).`;

export const searchToolAtom = atom(() => ({
  search_tool: {
    description: searchToolDescription,
    parameters: SearchToolParams,
    shouldInclude: () => true,
    execute: async (args: any) => {
      return searchToolExecute(args);
    },
  },
}));

export const search_tool = {
  description: searchToolDescription,
  parameters: SearchToolParams,
  shouldInclude: () => true,
  execute: async (args: any) => {
    return searchToolExecute(args);
  },
};

export const search_tool_client_tool = makeClientTool({
  name: "search_tool",
  description: search_tool.description,
  parameters: search_tool.parameters,
  execute: search_tool.execute,
});

searchToolAtom.debugLabel = "searchToolAtom";

/* eslint-enable sort-exports/sort-exports */
